import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { OnboardingState } from '@ekhodealer/ekho-common';
import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import { GuardId, useGuardLoadingState } from '@ekhodealer/ekho-common/components';

import type { AdminGetMeResponse } from '@ekhodealer/ekho-common';
import type { ReactNode} from 'react';

import { getAuthFlowTarget } from '@common/lib/authRouting';

interface PortalAccessGuardProps {
	children: ReactNode;
}

/**
 * PortalAccessGuard
 * - Requires /me to be loaded
 * - Uses server-driven fields only (no claim decoding)
 * - Redirects based on onboarding_status, canAccessAdminPortal, and mfaPending
 *
 * Priority order:
 * 1. not authenticated -> /login
 * 2. canAccessAdminPortal === false -> /no-access
 * 3. onboarding_status != complete -> /setup/* (existing mapping)
 * 4. onboarding complete AND mfaPending === true -> /login (MFA is now a login state)
 * 5. else allow portal
 */
export function PortalAccessGuard({ children }: PortalAccessGuardProps) {
	const { user, me, isLoading, error, mfaPending } = useAuth();
	const adminMe = me as AdminGetMeResponse | null;
	const navigate = useNavigate();
	const location = useLocation();

	const isAuthLoading = isLoading || (!!user && !adminMe);

	// Loading guard for global LoadingPage overlay
	useGuardLoadingState(GuardId.AUTH, isAuthLoading);

	const shouldRedirectToLogin = !isLoading && !user;

	useEffect(() => {
		// Wait until me is available (or an error exists)
		if (isLoading || !user || !adminMe) return;

		// If server says no access, go to no-access
		if (adminMe.canAccessAdminPortal === false) {
			navigate('/no-access', { replace: true });
			return;
		}

		// If onboarding not complete, route to appropriate flow
		const target = getAuthFlowTarget(adminMe.onboardingState);
		if (target.kind === 'setup' || target.kind === 'mfa') {
			if (!location.pathname.startsWith(target.path)) {
				navigate(target.path, { replace: true });
			}
			return;
		}

		// If onboarding is complete but MFA challenge is pending, redirect to /login
		// (MFA challenge is now handled as a login state on the /login route)
		if (adminMe.onboardingState === 'complete' && mfaPending) {
			navigate('/login', { replace: true });
			return;
		}

		// If we have an auth error indicating forbidden, treat as no-access.
		// (AuthSessionProvider will handle 401 with signout+redirect.)
		if (error?.code === 'me/403' || error?.code === 'api/403') {
			navigate('/no-access', { replace: true });
		}
	}, [isLoading, user, adminMe, error, mfaPending, navigate, location.pathname]);

	// Auth + access: if not logged in, go to /login (avoid infinite loading when /me is disabled).
	if (shouldRedirectToLogin) {
		return <Navigate to='/login' replace state={{ from: location }} />;
	}

	// Block rendering while loading /me
	if (isAuthLoading || !adminMe) return null;

	// If MFA challenge pending, block portal access
	if (mfaPending) {
		return null;
	}

	// If onboarding complete and access granted, allow
	if (adminMe.onboardingState === OnboardingState.COMPLETE && adminMe.canAccessAdminPortal) {
		return <>{children}</>;
	}

	return null;
}

export default PortalAccessGuard;
